// @ts-strict-ignore
import {Component, Injector} from "@angular/core";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Gender, LoginType, Permissions, SimRole, SimUser} from "@models";
import {CustomValidationService, SimMaterialDialogService, NotificationService, SimSystemService, SimUserExtendedService} from "@services";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {MatCheckboxChange} from "@angular/material/checkbox";

export interface DialogData_CreateSimUserDialogInput extends MatDialogDataBaseInput {
	simUser?: SimUser;
}

export interface DialogData_CreateSimUserDialogOutput extends MatDialogDataBaseOutput {
	followUpAction: "userUpdated" | "userCreated";
}

@Component({
	selector: "sim-create-sim-user-dialog",
	templateUrl: "./create-sim-user-dialog.component.html",
	styleUrls: ["./create-sim-user-dialog.component.scss"],
	standalone: false,
})
export class CreateSimUserDialogComponent extends BasicMaterialDialog<
	CreateSimUserDialogComponent,
	DialogData_CreateSimUserDialogInput,
	DialogData_CreateSimUserDialogOutput
> {
	createUserFormGroup = new FormGroup({
		username: new FormControl("", [Validators.required, this._customValidationService.noSpacesValidator()]),
		email: new FormControl("", [Validators.required, Validators.email, this._customValidationService.noSpacesValidator()]),
		firstName: new FormControl("", [Validators.required]),
		lastName: new FormControl("", [Validators.required]),
		birthDay: new FormControl<Date>(null),
		gender: new FormControl<Gender>(Gender.Other, [Validators.required]),
		contactNumber: new FormControl(""),
		studentId: new FormControl(""),
	});

	passwordFormGroup: FormGroup<{
		password: FormControl<string>;
		confirmPassword: FormControl<string>;
	}>;
	LoginType = LoginType;
	hidePasswordNew: boolean = true;
	public simRole: SimRole[] = [];
	public checkedRoles: string[] = [];
	public currentUser = this._simUserService.user;
	public permissionToChangeAllPassword: boolean = false;
	public updateUserPermission: boolean = false;

	constructor(
		_injector: Injector,
		fb: FormBuilder,
		private _simDialogService: SimMaterialDialogService,
		private _customValidationService: CustomValidationService,
		private _simNotificationService: NotificationService,
		private _simSystemService: SimSystemService,
		private _simUserService: SimUserExtendedService,
	) {
		super(_injector);

		this.passwordFormGroup = fb.group(
			{
				password: fb.control("", [Validators.required, this._customValidationService.passwordValidator()]),
				confirmPassword: fb.control("", [Validators.required]),
			},
			{
				validators: [this._customValidationService.matchPassword("password", "confirmPassword")],
			},
		);

		if (this.data.simUser) {
			this.initializeFormWithUserData();
		}
	}

	async ngOnInit() {
		await this.getSimRoles();
		this.permissionToChangeAllPassword = this._simUserService.hasPermission(Permissions.UserManagement_PermissionToChangeAllPasswords);

		this.updateUserPermission = this._simUserService.hasPermission(Permissions.UserManagement_EditAll);
	}

	okClicked() {
		if (this.data.simUser) {
			this.data.simUser.username = this.createUserFormGroup.value.username;
			this.data.simUser.firstname = this.createUserFormGroup.value.firstName;
			this.data.simUser.lastname = this.createUserFormGroup.value.lastName;
			this.data.simUser.email = this.createUserFormGroup.value.email;
			this.data.simUser.gender = this.createUserFormGroup.value.gender;
			this.data.simUser.isicNumber = this.createUserFormGroup.value.studentId;
			this.data.simUser.phoneNumber = this.createUserFormGroup.value.contactNumber;
			this.data.simUser.birthday = this.createUserFormGroup.value.birthDay;

			this.data.simUser.roleIds = [];
			this.data.simUser.roleIds = this.checkedRoles;

			this._simUserService
				.updateUser({
					user: this.data.simUser,
					password: "",
					imageBytes: "",
				})
				.then((updateUserRes) => {
					if (updateUserRes.success) {
						this.close({
							followUpAction: "userUpdated",
							action: "ok",
						});
					} else {
						this._simNotificationService.notifyError(updateUserRes.error);
					}
				});
		} else {
			//Create user

			let roles: string[] = [];

			roles = this.checkedRoles;

			if (roles.length === 0) {
				this._simNotificationService.notifyError("At least one role is required");
				return;
			}

			let birthDay = null;
			if (this.createUserFormGroup.value.birthDay !== null) {
				birthDay = this.createUserFormGroup.value.birthDay;
			}

			const {email, username, firstName, lastName, gender, contactNumber, studentId} = this.createUserFormGroup.value;

			this._simUserService
				.createUser({
					username: username,
					email: email,
					firstName: firstName,
					lastName: lastName,
					birthday: birthDay,
					gender: gender,
					phoneNumber: contactNumber,
					isicNumber: studentId,
					roles: roles,
					password: this.passwordFormGroup.value.password,
				})
				.then((res) => {
					if (res.success === false) {
						this._simNotificationService.notifyError(res.error);
					} else {
						this.close({
							action: "ok",
							followUpAction: "userCreated",
						});
					}
				});
		}
	}

	changePasswordClicked() {
		this._simDialogService.openChangePasswordDialog({
			userForPasswordChange: this.data.simUser,
		});
	}

	roleChanged(role: string, $event: MatCheckboxChange) {
		let checked = $event.checked;
		if (checked) {
			this.checkedRoles.push(role);
		} else {
			const index = this.checkedRoles.indexOf(role, 0);
			if (index > -1) {
				this.checkedRoles.splice(index, 1);
			}
		}
	}

	private async getSimRoles() {
		this.simRole = (await this._simSystemService.getSimRolesForUpsertUser()).filter((x) => !x.isHidden);
	}

	private initializeFormWithUserData() {
		const {username, email, firstname, lastname, birthday, gender, phoneNumber, isicNumber} = this.data.simUser;

		this.createUserFormGroup.patchValue({
			username: username,
			firstName: firstname,
			lastName: lastname,
			birthDay: birthday ? new Date(birthday) : null,
			contactNumber: phoneNumber,
			studentId: isicNumber,
			email: email,
			gender: gender,
		});

		//Set the Roles
		this.checkedRoles = this.data.simUser.roleIds;
	}
}
